import { inject } from '@angular/core';
import { SettingsService } from '../core/services/settings.service';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  DEFAULT_STUDENT_TEST_LANGUAGE,
  StudentTestLanguage,
  UserSettings,
  UserSettingsPatch,
} from '../core/models/setting.model';

export enum NumpadPosition {
  None = 0,
  Left = 1,
  Right = 2,
}

export enum ApplicationIdentificator {
  Bareka = 1,
  Rekensprint = 2,
  ATR = 3,
}

/**
 * Returns true if the given value identifies the supplied application.
 * Accepts both the numeric enum (e.g. 2) and the string name (e.g. "Rekensprint")
 * because the backend Setting.Application is serialized via JsonStringEnumConverter
 * over SignalR while the REST/local code paths use the numeric enum.
 */
export function isApplicationMatch(
  value: unknown,
  app: ApplicationIdentificator
): boolean {
  return value === app || value === ApplicationIdentificator[app];
}

export enum StringStrategyPowerTasksOptions {
  None = 0,
  On = 1,
  Off = 2,
  Automatic = 3,
}

interface SettingsState {
  showTimerDuringSpeedTasks: boolean;
  showTimer: boolean;
  unlimitedTestTime: boolean;
  unlimitedPracticeTasksSpeedTasks: boolean;
  showNumPad: boolean;
  padPosition: NumpadPosition;
  readSumsAloud: boolean;
  stringStrategyPowerTasksOptions: StringStrategyPowerTasksOptions;
  language: StudentTestLanguage;
  pauseTaskWhenIdle: boolean;
  showInstructionVideo: boolean;
  isLoading: boolean;
  isLoaded: boolean;
}

const initialSettingsState: SettingsState = {
  showTimerDuringSpeedTasks: false,
  showTimer: false,
  unlimitedTestTime: false,
  unlimitedPracticeTasksSpeedTasks: false,
  showNumPad: false,
  padPosition: NumpadPosition.None,
  readSumsAloud: false,
  stringStrategyPowerTasksOptions: StringStrategyPowerTasksOptions.Off,
  language: DEFAULT_STUDENT_TEST_LANGUAGE,
  pauseTaskWhenIdle: false,
  showInstructionVideo: true,
  isLoading: false,
  isLoaded: false,
};

export const SettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialSettingsState),
  withMethods((store, settingsService = inject(SettingsService)) => ({
    async loadSettings(application: ApplicationIdentificator) {
      try {
        patchState(store, { isLoading: true });
        const settings = await settingsService.getSettings(application);
        // A user with no Settings row yet gets a null body; coalesce to the
        // defaults so "no row" is not mistaken for a configured value.
        const numpadPosition = settings?.numpadPosition ?? NumpadPosition.None;
        patchState(store, {
          showTimerDuringSpeedTasks: settings?.showTimerDuringSpeedTasks ?? false,
          showTimer: settings?.showTimer ?? false,
          unlimitedTestTime: settings?.unlimitedTestTime ?? false,
          unlimitedPracticeTasksSpeedTasks: settings?.unlimitedPracticeTasksSpeedTasks ?? false,
          readSumsAloud: settings?.readSumsAloud ?? false,
          showNumPad: numpadPosition !== NumpadPosition.None,
          padPosition: numpadPosition,
          stringStrategyPowerTasksOptions:
            settings?.stringStrategyPowerTasksOptions ?? StringStrategyPowerTasksOptions.Off,
          language: settings?.language ?? DEFAULT_STUDENT_TEST_LANGUAGE,
          pauseTaskWhenIdle: settings?.pauseTaskWhenIdle ?? false,
          showInstructionVideo: settings?.showInstructionVideo ?? true,
          isLoading: false,
          isLoaded: true,
        });
      } catch {
        patchState(store, { isLoading: false });
      }
    },

    async saveSettings(payload: UserSettings) {
      try {
        patchState(store, { isLoading: true });
        await settingsService.saveSettings(payload);
      } finally {
        patchState(store, { isLoading: false });
      }
    },

    /**
     * Turns the numpad on (Right) and persists it when it is currently off.
     * Guarded on isLoaded: state after a failed load is indistinguishable from a
     * real "everything off" config, and saveSettings overwrites every field.
     */
    async ensureNumpadEnabled(application: ApplicationIdentificator) {
      if (!store.isLoaded() || store.padPosition() !== NumpadPosition.None) return;

      const payload: UserSettings = {
        showTimerDuringSpeedTasks: store.showTimerDuringSpeedTasks(),
        showTimer: store.showTimer(),
        readSumsAloud: store.readSumsAloud(),
        unlimitedTestTime: store.unlimitedTestTime(),
        unlimitedPracticeTasksSpeedTasks: store.unlimitedPracticeTasksSpeedTasks(),
        stringStrategyPowerTasksOptions: store.stringStrategyPowerTasksOptions(),
        language: store.language(),
        pauseTaskWhenIdle: store.pauseTaskWhenIdle(),
        showInstructionVideo: store.showInstructionVideo(),
        numpadPosition: NumpadPosition.Right,
        application,
      };

      // Applied locally first so the numpad is usable this session even if the
      // request fails; the next session retries.
      patchState(store, { showNumPad: true, padPosition: NumpadPosition.Right });
      await settingsService.saveSettings(payload);
    },

    async saveSettingsByUserId(userId: string, payload: UserSettings) {
      try {
        patchState(store, { isLoading: true });
        await settingsService.saveSettingsByUserId(userId, payload);
      } finally {
        patchState(store, { isLoading: false });
      }
    },

    async saveSettingsByUserIds(userIds: string[], payload: UserSettingsPatch) {
      try {
        patchState(store, { isLoading: true });
        await settingsService.saveSettingsByUserIds(userIds, payload);
      } finally {
        patchState(store, { isLoading: false });
      }
    },
    setSettings(payload: UserSettings) {
      try {
        const currentState = {
          showTimerDuringSpeedTasks: payload.showTimerDuringSpeedTasks,
          showTimer: payload.showTimer,
          readSumsAloud: payload.readSumsAloud,
          numpadPosition: payload.numpadPosition,
          application: payload.application,
          unlimitedTestTime: payload.unlimitedTestTime,
          unlimitedPracticeTasksSpeedTasks: payload.unlimitedPracticeTasksSpeedTasks,
          stringStrategyPowerTasksOptions: payload.stringStrategyPowerTasksOptions,
          language: payload.language ?? DEFAULT_STUDENT_TEST_LANGUAGE,
          pauseTaskWhenIdle: payload.pauseTaskWhenIdle,
          showInstructionVideo: payload.showInstructionVideo ?? true,
        };
        patchState(store, currentState, { isLoading: true });
      } finally {
        patchState(store, { isLoading: false });
      }
    },

    /**
     * Applies settings received from SignalR push notification.
     * Does not trigger loading state since this is a passive update.
     */
    applyPushedSettings(payload: UserSettings) {
      patchState(store, {
        showTimerDuringSpeedTasks: payload.showTimerDuringSpeedTasks,
        showTimer: payload.showTimer,
        readSumsAloud: payload.readSumsAloud,
        unlimitedTestTime: payload.unlimitedTestTime,
        unlimitedPracticeTasksSpeedTasks: payload.unlimitedPracticeTasksSpeedTasks,
        showNumPad: payload.numpadPosition != NumpadPosition.None,
        padPosition: payload.numpadPosition,
        stringStrategyPowerTasksOptions: payload.stringStrategyPowerTasksOptions,
        language: payload.language ?? DEFAULT_STUDENT_TEST_LANGUAGE,
        pauseTaskWhenIdle: payload.pauseTaskWhenIdle,
        showInstructionVideo: payload.showInstructionVideo ?? true,
        isLoaded: true,
      });
    },
  }))
);
